import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "@/lib/api";
import { X, ShieldCheck, Sparkles } from "lucide-react";

const WS_URL = BACKEND_URL.replace(/^http/, "ws") + "/api/ws/activity";
const DISMISS_KEY = "connexis_pops_dismissed";
const FIRST_DELAY_MS = 6000;
const SHOW_MS = 6500;
const GAP_MIN_MS = 9000;
const GAP_MAX_MS = 17000;

// Pages with a fixed bottom-left LiveReaders pill — stack the pop above it.
const RAISED_PATHS = new Set(["/pricing", "/why-connexis", "/inside-connexis"]);

function relTime(ts) {
  if (!ts) return "just now";
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

/** Social-proof toast pops — consumes the same anonymised public activity
 * feed as /activity (mode-aware: simulated / blended / real).
 * Runs continuously while the visitor stays on the site; queue refills
 * from the feed + live WebSocket events. */
export default function LiveLeadPops() {
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();
  const queueRef = useRef([]);
  const shownRef = useRef(new Set());
  const timersRef = useRef([]);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    let mounted = true;
    const later = (fn, ms) => { const t = setTimeout(fn, ms); timersRef.current.push(t); };

    const refill = () => {
      axios.get(`${BACKEND_URL}/api/public/activity?limit=30`).then((r) => {
        if (!mounted) return;
        const evs = r.data?.events || [];
        const fresh = evs.filter((e) => !shownRef.current.has(`${e.type}:${e.id}`));
        if (fresh.length) {
          queueRef.current = fresh;
        } else {
          shownRef.current.clear();
          queueRef.current = evs;
        }
      }).catch(() => {});
    };
    refill();

    let ws;
    try {
      ws = new WebSocket(WS_URL);
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev?.type) queueRef.current = [ev, ...queueRef.current].slice(0, 40);
        } catch {}
      };
    } catch {}
    const pingId = setInterval(() => {
      try { if (ws?.readyState === 1) ws.send("ping"); } catch {}
    }, 25000);

    const showNext = () => {
      if (!mounted || stoppedRef.current) return;
      const ev = queueRef.current.shift();
      if (!ev) { refill(); later(showNext, 12000); return; }
      shownRef.current.add(`${ev.type}:${ev.id}`);
      if (queueRef.current.length < 3) refill();
      setCurrent(ev);
      later(() => setVisible(true), 30);
      later(() => {
        setVisible(false);
        later(() => setCurrent(null), 350);
        later(showNext, GAP_MIN_MS + Math.random() * (GAP_MAX_MS - GAP_MIN_MS));
      }, SHOW_MS);
    };
    later(showNext, FIRST_DELAY_MS);

    return () => {
      mounted = false;
      timersRef.current.forEach(clearTimeout);
      clearInterval(pingId);
      try { ws?.close(); } catch {}
    };
  }, []);

  const dismiss = () => {
    stoppedRef.current = true;
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setTimeout(() => setCurrent(null), 350);
  };

  if (!current) return null;
  if (pathname === "/lending-network") return null;   // the network page sells on its own terms
  if (pathname.startsWith("/business-funding")) return null;   // an applicant reading this doesn't need lead-gen pops
  const isAi = current.type === "ai.qualified";
  const smBottom = RAISED_PATHS.has(pathname) ? "sm:bottom-[70px]" : "sm:bottom-5";

  return (
    <div
      data-testid="live-pop"
      className={`fixed bottom-3 left-3 right-3 sm:left-5 sm:right-auto sm:w-[350px] ${smBottom} z-[60] transition-all duration-300 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <div className="relative rounded-2xl border border-slate-200 bg-white shadow-float p-4 pr-9">
        <button
          onClick={dismiss}
          data-testid="live-pop-dismiss"
          aria-label="Dismiss notifications"
          className="absolute top-2.5 right-2.5 grid place-items-center size-6 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <X className="size-3.5" />
        </button>
        <div className="flex items-start gap-3">
          <span className="relative grid place-items-center size-10 rounded-full bg-slate-900 text-white shrink-0 font-mono text-[12px] font-semibold">
            {isAi ? <Sparkles className="size-4" /> : (current.initial || "—")}
            <span className="absolute -top-0.5 -right-0.5 flex size-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full size-2.5 bg-emerald-500 ring-2 ring-white" />
            </span>
          </span>
          <div className="min-w-0">
            <p className="text-[13px] text-slate-700 leading-snug">
              {isAi ? (
                <>AI just qualified a <span className="font-semibold text-slate-900">{current.sector_name}</span> enquiry in <span className="font-semibold text-slate-900">{current.city}</span></>
              ) : (
                <><span className="font-semibold text-slate-900">{current.initial} in {current.city}</span> just requested a <span className="font-semibold text-slate-900">{current.sector_name}</span> quote</>
              )}
            </p>
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-400">
              <ShieldCheck className="size-3 text-emerald-500" />
              <span>Verified routing event</span>
              <span>·</span>
              <span className="tabular-nums">{relTime(current.ts)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
